import { useState } from 'react';
import { apiClient } from '@/lib/api-client';

/**
 * 确保用户有推送订阅
 * 如果用户已授权但数据库没有订阅记录，则自动创建并保存
 *
 * @returns Promise<{ success: boolean; needsPermission?: boolean; error?: string }>
 */
export async function ensureSubscription(): Promise<{
  success: boolean;
  needsPermission?: boolean;
  alreadyExists?: boolean;
  error?: string;
}> {
  try {
    // 1. 检查权限状态
    if (!('Notification' in window)) {
      console.log('[EnsureSubscription] Browser does not support notifications');
      return { success: false, needsPermission: true, error: 'Browser not supported' };
    }

    if (Notification.permission !== 'granted') {
      console.log('[EnsureSubscription] Permission not granted');
      return { success: false, needsPermission: true };
    }

    console.log('[EnsureSubscription] Permission granted, checking database...');

    // 2. 检查数据库是否有订阅记录
    const response = await apiClient.get('/api/push/subscribe');

    if (!response.ok) {
      console.error('[EnsureSubscription] Failed to check database');
      return { success: false, error: 'Failed to check database' };
    }

    const data = await response.json();

    if (data.isSubscribed && data.count > 0) {
      console.log('[EnsureSubscription] ✅ Subscription already exists');
      return { success: true, alreadyExists: true };
    }

    // 3. 数据库没有订阅，创建并保存
    console.log('[EnsureSubscription] No subscription in database, creating...');
    const result = await subscribeAndSaveToDB();

    return result;

  } catch (error) {
    console.error('[EnsureSubscription] Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * 订阅推送并保存到数据库
 * 内部函数，由 ensureSubscription 和 handleEnableNotifications 调用
 */
async function subscribeAndSaveToDB(): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('[PushNotification] 🚀 Subscribing to push notifications...');

    // 1. 动态导入 pushNotificationService 并订阅
    const { pushNotificationService } = await import('@/lib/pushNotifications');
    const subscription = await pushNotificationService.subscribe();

    console.log('[PushNotification] 📦 Subscription:', subscription);

    if (!subscription) {
      console.error('[PushNotification] ❌ No subscription object returned');
      return { success: false, error: 'Failed to create subscription' };
    }

    console.log('[PushNotification] 💾 Saving to database...');

    // 2. 保存到数据库
    const response = await apiClient.post('/api/push/subscribe', {
      subscription,
      action: 'subscribe',
      deviceName: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[PushNotification] ❌ Failed to save:', errorData);
      return { success: false, error: errorData.error || 'Failed to save to database' };
    }

    const data = await response.json();
    console.log('[PushNotification] 🎉 Database response:', data);
    console.log('[PushNotification] ✅ Push notification enabled successfully!');

    return { success: true };

  } catch (error) {
    console.error('[PushNotification] ❌ Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * 通用的通知权限管理 Hook
 *
 * @example
 * ```tsx
 * const { showModal, setShowModal, handleEnableNotifications } = useNotificationPermission();
 *
 * // 在需要提示的地方
 * setShowModal(true);
 *
 * // 在组件中渲染弹窗
 * <NotificationPermissionModal
 *   visible={showModal}
 *   onClose={() => setShowModal(false)}
 *   onEnable={handleEnableNotifications}
 * />
 * ```
 */
export const useNotificationPermission = () => {
  const [showModal, setShowModal] = useState(false);

  /**
   * 请求通知权限并订阅 Web Push
   */
  const handleEnableNotifications = async () => {
    try {
      console.log('[NotificationHook] 🚀 Starting notification enablement...');

      // 检查浏览器是否支持通知
      if (!('Notification' in window)) {
        console.warn('[NotificationHook] ❌ Browser does not support notifications');
        setShowModal(false);
        return;
      }

      console.log('[NotificationHook] 📝 Requesting permission...');
      // 请求权限
      const permission = await Notification.requestPermission();
      console.log('[NotificationHook] 📋 Permission:', permission);

      if (permission === 'granted') {
        console.log('[NotificationHook] ✅ Permission granted, subscribing...');

        // 调用提取的函数
        const result = await subscribeAndSaveToDB();

        if (!result.success) {
          console.error('[NotificationHook] ❌ Failed:', result.error);
        }
      } else if (permission === 'denied') {
        console.log('[NotificationHook] ❌ Notification permission denied');
      } else {
        console.log('[NotificationHook] ⚠️ Unknown permission state:', permission);
      }
    } catch (error) {
      console.error('[NotificationHook] ❌ Error:', error);
      if (error instanceof Error) {
        console.error('[NotificationHook] Error details:', error.message, error.stack);
      }
    } finally {
      setShowModal(false);
    }
  };

  /**
   * 检查是否应该显示通知权限提示
   * 在浏览器支持且权限未授予时返回 true (包括 default 和 denied)
   */
  const shouldPromptNotification = () => {
    if (typeof window === 'undefined') return false;
    return 'Notification' in window && Notification.permission !== 'granted';
  };

  /**
   * 检查通知权限状态
   */
  const getPermissionStatus = () => {
    if (typeof window === 'undefined') return 'unsupported';
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  };

  return {
    showModal,
    setShowModal,
    handleEnableNotifications,
    shouldPromptNotification,
    getPermissionStatus
  };
};

export default useNotificationPermission;
